import {
  RANGE_PRESETS,
  presetLabel,
  rangeDays,
  validateRangeSelection,
  type RangePreset,
} from '@anytracker/core'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Button } from '../components/Button.js'
import { formatDay } from '../format/dates.js'
import styles from './Range.module.css'
import { useRange } from './RangeProvider.js'
import { readRangeParams, writeRangeParams } from './url.js'

/**
 * The shared date range control (T-402).
 *
 * Two things it always shows, both deliberate:
 *
 *  - **The resolved dates, next to the preset name.** "Last 30 days" on a chart
 *    you are looking at now is fine; on an export opened six weeks later it is
 *    meaningless. Keeping the words and the dates together here means they are
 *    never separated, and T-504 carries the same pair into the report.
 *  - **How many days that is.** The difference between a 7-day and a 90-day
 *    window is the difference between a shape and a trend, and nothing else on
 *    a chart says which one you are reading.
 */
export function RangeControl({ empty = false }: { empty?: boolean }) {
  const { selection, setSelection, resolved } = useRange()
  const [search, setSearch] = useSearchParams()
  const [custom, setCustom] = useState({ from: resolved.fromDay, to: resolved.toDay })
  const [error, setError] = useState<string | null>(null)

  // The URL wins on arrival, once, so a bookmarked or shared link opens the
  // range it was saved with rather than whatever this session last looked at.
  const adopted = useRef(false)
  useEffect(() => {
    if (adopted.current) {
      return
    }
    adopted.current = true
    const fromUrl = readRangeParams(search)
    if (fromUrl !== null && validateRangeSelection(fromUrl).ok) {
      setSelection(fromUrl)
    }
  }, [search, setSelection])

  // Presets move the pickers; the pickers are what a custom range is made of.
  useEffect(() => {
    setCustom({ from: resolved.fromDay, to: resolved.toDay })
  }, [resolved.fromDay, resolved.toDay])

  function choose(preset: RangePreset) {
    setError(null)
    setSelection({ kind: 'preset', preset })
    setSearch(writeRangeParams(search, { kind: 'preset', preset }), { replace: true })
  }

  function applyCustom(next: { from: string; to: string }) {
    setCustom(next)
    const candidate = { kind: 'custom', fromDay: next.from, toDay: next.to } as const
    const checked = validateRangeSelection(candidate)
    if (!checked.ok) {
      // Reported, never swapped: reversing the dates hides the typo in the
      // field that caused it, and the user gets a range they did not ask for
      // with no clue which end was wrong.
      setError(checked.errors[0]?.message ?? 'That range does not work.')
      return
    }
    setError(null)
    setSelection(candidate)
    setSearch(writeRangeParams(search, candidate), { replace: true })
  }

  const days = rangeDays(resolved)

  return (
    <div className={styles['range']}>
      <div className={styles['presets']} role="group" aria-label="Date range">
        {RANGE_PRESETS.map((preset) => {
          const active = selection.kind === 'preset' && selection.preset === preset
          return (
            <Button
              key={preset}
              variant={active ? 'primary' : 'ghost'}
              aria-pressed={active}
              onClick={() => {
                choose(preset)
              }}
            >
              {presetLabel(preset)}
            </Button>
          )
        })}
      </div>

      <div className={styles['custom']}>
        <label className={styles['dayField']}>
          <span>From</span>
          <input
            type="date"
            value={custom.from}
            aria-label="Range start"
            onChange={(event) => {
              applyCustom({ ...custom, from: event.target.value })
            }}
          />
        </label>
        <label className={styles['dayField']}>
          <span>To</span>
          <input
            type="date"
            value={custom.to}
            aria-label="Range end"
            onChange={(event) => {
              applyCustom({ ...custom, to: event.target.value })
            }}
          />
        </label>
      </div>

      {error !== null && (
        <p className={styles['error']} role="alert">
          {error}
        </p>
      )}

      <p className={styles['resolved']} data-testid="resolved-range">
        <strong>{resolved.label}</strong>
        {': '}
        {formatDay(resolved.fromDay)} – {formatDay(resolved.toDay)}
        <span className={styles['days']}>
          {days === 1 ? '1 day' : `${String(days)} days`}
        </span>
      </p>

      {empty && (
        <p className={styles['noData']} role="status">
          Nothing was recorded in this range.
        </p>
      )}
    </div>
  )
}
